import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../pages/Dashboard.css';
import { Icon, IconName, CopyButton } from '../components/ui';
import PieChart from '../components/PieChart';
import DashboardDrill, { DrillFrame } from '../components/DashboardDrill';
import { NumberTicker } from '../components/magicui/number-ticker';
import { BorderBeam } from '../components/magicui/border-beam';
import { DotPattern } from '../components/magicui/dot-pattern';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

// Semantic color for a request status (mirrors DashboardDrill's mapping) — used
// by the "Request Outcomes" pie so allow / deny / error read at a glance.
const statusColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'success' || s === 'policy_allowed' || s === 'policy_advisory_allow' || s === 'proxied' || s === 'accepted') return 'var(--success)';
  if (s === 'error' || s === 'upstream_error') return 'var(--danger)';
  if (s.includes('den') || s.includes('block')) return 'var(--danger)';
  if (s.includes('warn') || s.includes('anomal') || s.includes('flag')) return 'var(--warning)';
  return 'var(--accent)';
};

interface DashboardProps {
  apiUrl: string;
}

interface DailyBucket {
  date: string;
  total: number;
  success: number;
  violations: number;
  errors: number;
}

interface ToolBreakdown {
  tool: string;
  count: number;
  success: number;
  errors: number;
}

interface ServiceBreakdown {
  service: string;
  count: number;
}

interface StatusBreakdown {
  status: string;
  count: number;
}

interface RecentEvent {
  id: number;
  timestamp: string;
  service: string;
  tool_name: string;
  status: string;
  agent_id: string;
  result: string;
}

interface EnhancedStats {
  total_requests: number;
  total_violations: number;
  total_anomalies: number;
  active_agents: number;
  pending_agents: number;
  total_agents: number;
  errors: number;
  success_rate: number;
  daily: DailyBucket[];
  by_service: ServiceBreakdown[];
  by_tool: ToolBreakdown[];
  by_agent: { agent_id: string; count: number }[];
  by_status: StatusBreakdown[];
  agents_by_status: { status: string; count: number }[];
  agents_by_role: { role: string; count: number }[];
  hourly: number[];
  recent: RecentEvent[];
}

const formatHourLabel = (hour: number, short = true): string => {
  const h = hour % 24;
  if (short) {
    if (h === 0) return '12a';
    if (h < 12) return `${h}a`;
    if (h === 12) return '12p';
    return `${h - 12}p`;
  }
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
};

const buildSmoothPath = (xs: number[], ys: number[]): string => {
  if (xs.length === 0) return '';
  if (xs.length === 1) return `M ${xs[0]},${ys[0]}`;

  let d = `M ${xs[0]},${ys[0]}`;
  for (let i = 0; i < xs.length - 1; i++) {
    const p0x = i > 0 ? xs[i - 1] : xs[i];
    const p0y = i > 0 ? ys[i - 1] : ys[i];
    const p1x = xs[i];
    const p1y = ys[i];
    const p2x = xs[i + 1];
    const p2y = ys[i + 1];
    const p3x = i + 2 < xs.length ? xs[i + 2] : xs[i + 1];
    const p3y = i + 2 < ys.length ? ys[i + 2] : ys[i + 1];

    const cp1x = p1x + (p2x - p0x) / 6;
    const cp1y = p1y + (p2y - p0y) / 6;
    const cp2x = p2x - (p3x - p1x) / 6;
    const cp2y = p2y - (p3y - p1y) / 6;

    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2x},${p2y}`;
  }
  return d;
};

const statusBadgeClass = (status: string): string => {
  const s = status.toLowerCase();
  if (s === 'success' || s === 'policy_allowed') return 'allow';
  if (s === 'error' || s.includes('den') || s.includes('block')) return 'deny';
  if (s.includes('warn') || s.includes('anomal')) return 'warn';
  return 'neutral';
};

const trackKpiClick = (label: string, to: string) => {
  const payload = { label, to, timestamp: Date.now() };

  window.dispatchEvent(new CustomEvent('ztai:kpi-click', { detail: payload }));

  const w = window as any;
  if (typeof w.gtag === 'function') {
    w.gtag('event', 'kpi_card_click', {
      event_category: 'dashboard',
      event_label: label,
      destination: to,
    });
  }

  if (Array.isArray(w.dataLayer)) {
    w.dataLayer.push({ event: 'kpi_card_click', ...payload });
  }
};

const Dashboard: React.FC<DashboardProps> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const { t: tr } = useTranslation();
  const [stats, setStats] = useState<EnhancedStats | null>(null);
  const [mcpServerCount, setMcpServerCount] = useState<{ total: number; healthy: number }>({ total: 0, healthy: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [trendRange, setTrendRange] = useState<24 | 7 | 14 | 30>(14);
  const [trendHoverIndex, setTrendHoverIndex] = useState<number | null>(null);
  const [trendPinnedIndex, setTrendPinnedIndex] = useState<number | null>(null);
  const [hourlyHoverIndex, setHourlyHoverIndex] = useState<number | null>(null);
  const [drill, setDrill] = useState<DrillFrame[]>([]);
  // Keep a ref in sync so the stats poll can pause itself while a drill is open.
  const drillOpenRef = useRef(false);
  useEffect(() => { drillOpenRef.current = drill.length > 0; }, [drill]);

  const pushDrill = (frame: DrillFrame) => setDrill((s) => [...s, frame]);

  const getRiskScore = (violations: number, anomalies: number, total: number): number => {
    if (total === 0) return 0;
    return Math.min(100, Math.round((violations / Math.max(total, 1)) * 100 + anomalies * 5));
  };

  const getRiskLevel = (score: number): { level: string; color: string } => {
    if (score >= 85) return { level: t('pages.dashboard.riskHighlyCritical'), color: '#E40046' };
    if (score >= 68) return { level: t('pages.dashboard.riskCritical'), color: '#EA5D5D' };
    if (score >= 51) return { level: t('pages.dashboard.riskHigh'), color: '#F9CF8B' };
    if (score >= 34) return { level: t('pages.dashboard.riskMedium'), color: '#F5A623' };
    if (score >= 17) return { level: t('pages.dashboard.riskLow'), color: '#79C6C7' };
    return { level: t('pages.dashboard.riskVeryLow'), color: '#00B88A' };
  };

  useEffect(() => {
    const fetchData = async () => {
      // Pause background refresh while a drilldown is open so the surface
      // (and the underlying grid state we return to) isn't churned.
      if (drillOpenRef.current) return;
      try {
        const [statsResp, mcpResp] = await Promise.all([
          fetch(`${apiUrl}/admin/stats`),
          fetch(`${apiUrl}/admin/mcp/available-services`),
        ]);

        if (statsResp.ok) {
          const data = await statsResp.json();
          setStats(data);
        }

        if (mcpResp.ok) {
          const mcpData = await mcpResp.json();
          const services = mcpData.services || [];
          const healthyCount = services.filter((s: any) => s.health_status === 'healthy' || !s.health_status).length;
          setMcpServerCount({ total: services.length, healthy: healthyCount });
        }

        setError(null);
        setLastUpdated(new Date());
      } catch (err) {
        setError(`Failed to fetch dashboard data: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [apiUrl]);

  const daily = stats?.daily || [];
  const byTool = stats?.by_tool || [];
  const byService = stats?.by_service || [];
  const byStatus = stats?.by_status || [];
  const agentsByStatus = stats?.agents_by_status || [];
  const agentsByRole = stats?.agents_by_role || [];
  const hourly = stats?.hourly || new Array(24).fill(0);
  const recent = stats?.recent || [];
  const trendData = trendRange === 24 
    ? hourly.map((count, i) => ({ date: formatHourLabel(i, false), total: count, violations: 0, errors: 0 }))
    : daily.slice(-trendRange);

  useEffect(() => {
    setTrendHoverIndex(null);
    setTrendPinnedIndex(null);
  }, [trendRange, daily.length]);

  // ── Drilldown entry points ──
  // pie status → break down by service, then tool, then records.
  const drillStatus = (rawStatus: string) => {
    trackKpiClick(`drill:status:${rawStatus}`, 'drill');
    pushDrill({
      filters: [{ field: 'status', value: rawStatus, label: rawStatus.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) }],
      chain: ['service', 'tool_name'],
      crumb: rawStatus.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    });
  };
  // tool → break down by outcome, then records.
  const drillTool = (tool: string) => {
    trackKpiClick(`drill:tool:${tool}`, 'drill');
    pushDrill({ filters: [{ field: 'tool_name', value: tool, label: tool }], chain: ['status'], crumb: tool });
  };
  // service → break down by tool, then records.
  const drillService = (service: string) => {
    trackKpiClick(`drill:service:${service}`, 'drill');
    pushDrill({ filters: [{ field: 'service', value: service, label: service }], chain: ['tool_name'], crumb: service });
  };
  // trend point → straight to Graph+Table, windowed to that hour/day.
  const drillTrend = (idx: number) => {
    const point = trendData[idx];
    if (!point) return;
    let timeWindow: { earliest: string; latest?: string };
    if (trendRange === 24) {
      const hoursAgo = 23 - idx; // index 23 = current hour
      timeWindow = { earliest: `${hoursAgo + 1}h`, latest: `${hoursAgo}h` };
    } else {
      timeWindow = { earliest: `${point.date}T00:00:00`, latest: `${point.date}T23:59:59` };
    }
    trackKpiClick(`drill:trend:${point.date}`, 'drill');
    pushDrill({ filters: [], chain: [], timeWindow, crumb: `Trend · ${point.date}` });
  };

  if (loading && !stats) {
    return (
      <div className="db-page">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col gap-6"
        >
          <div className="skeleton-header" />
          <div className="skeleton-grid">
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="skeleton-card"
              />
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  const riskScore = getRiskScore(stats?.total_violations || 0, stats?.total_anomalies || 0, stats?.total_requests || 0);
  const riskLevel = getRiskLevel(riskScore);
  const circumference = 2 * Math.PI * 46;
  const successRate = stats?.success_rate ?? 100;

  const kpis = [
    { label: t('pages.dashboard.totalRequests'), value: stats?.total_requests || 0, sub: t('pages.dashboard.allFirewallRequests'), icon: 'bolt' as IconName, featured: false, to: '/audit-logs', variant: 'info' as const },
    { label: t('pages.dashboard.violations'), value: stats?.total_violations || 0, sub: t('pages.dashboard.policyBreaches'), icon: 'warning' as IconName, featured: false, danger: (stats?.total_violations || 0) > 0, to: '/violations', variant: 'danger' as const },
    { label: t('pages.dashboard.activeAgents'), value: stats?.active_agents || 0, sub: t('pages.dashboard.pendingApprovalsSub', { count: stats?.pending_agents || 0 }), icon: 'users' as IconName, featured: false, to: '/agents', variant: 'accent' as const },
    { label: t('pages.dashboard.successRate'), value: successRate, sub: t('pages.dashboard.violationsSub', { count: stats?.total_violations || 0 }), icon: 'checkCircle' as IconName, featured: false, pct: true, to: '/audit-logs', variant: 'success' as const },
  ];

  const serverItems = [
    { name: t('pages.dashboard.serverAgentiFirewall'), state: 'Monitoring', healthy: true, icon: 'shield' as IconName },
    { name: t('pages.dashboard.serverAuditDatabase'), state: 'Connected', healthy: true, icon: 'logs' as IconName },
    { name: t('pages.dashboard.serverMcpServers'), state: `${mcpServerCount.healthy}/${mcpServerCount.total} healthy`, healthy: mcpServerCount.healthy > 0, icon: 'server' as IconName },
    { name: t('pages.dashboard.serverPolicyEngine'), state: 'Active', healthy: true, icon: 'settings' as IconName },
    { name: t('pages.dashboard.serverRateLimiter'), state: 'Enforcing', healthy: true, icon: 'bolt' as IconName },
  ];
  return (
    <div className="db-page">
      {/* Subtle background pattern */}
      <DotPattern className="[mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)] opacity-[0.03] dark:opacity-[0.04]" />
      {error && <div className="db-error"><Icon name="warning" />{error}</div>}

      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="db-header"
      >
        <div>
          <h1 className="db-title">{tr('pages.dashboard.dashboard')}</h1>
          <p className="db-subtitle">{tr('pages.dashboard.agenti5AnAdvancedFirewallGatewayBetween')}</p>
        </div>
        <div className="db-header-actions">
          <span className="db-updated"><span className="db-live-dot" />{t('pages.dashboard.live')} · {lastUpdated.toLocaleTimeString()}</span>
        </div>
      </motion.div>

      {/* ── 4 KPI cards ── */}
      <div className="db-stat-row">
        {kpis.map((kpi, index) => (
          <motion.div
            key={kpi.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <Link
              to={kpi.to}
              className={`db-stat db-stat-link db-stat-${kpi.variant} ${kpi.featured ? 'db-stat-featured' : ''} ${kpi.danger ? 'db-stat-danger' : ''} relative overflow-hidden`}
              aria-label={t('pages.dashboard.openKpiDetails', { label: kpi.label })}
              onClick={() => trackKpiClick(kpi.label, kpi.to)}
            >
              <BorderBeam size={150} duration={12} delay={index * 3} colorFrom={kpi.variant === 'danger' ? '#ff3b63' : kpi.variant === 'success' ? '#1db96b' : '#d9d1e2'} colorTo={kpi.variant === 'danger' ? '#ff6b8a' : kpi.variant === 'success' ? '#4ade80' : '#90B5DF'} />
              <div className="db-stat-top">
                <span className="db-stat-head-left">
                  <span className="db-stat-icon"><Icon name={kpi.icon} size={16} /></span>
                  <span className="db-stat-label">{kpi.label}</span>
                </span>
                <span className="db-stat-go" aria-hidden="true">
                  <Icon name="chevronRight" size={14} />
                </span>
              </div>
              <div className="db-stat-num">
                <NumberTicker value={kpi.value} className="text-inherit font-inherit" />{kpi.pct ? '%' : ''}
              </div>
              <div className="db-stat-sub">{kpi.sub}</div>
            </Link>
          </motion.div>
        ))}
      </div>

      {drill.length > 0 ? (
        <DashboardDrill
          apiUrl={apiUrl}
          stack={drill}
          onPush={pushDrill}
          onPopTo={(len) => setDrill((s) => s.slice(0, len))}
        />
      ) : (
      <>
      {/* ── Row 2: Request trend + Request Outcomes + Risk Score ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="db-mid-row"
      >
        {/* Request Trend Chart */}
        <div className="db-card db-analytics-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.requestTrend')}</h3>
            <div className="db-trend-controls" role="group" aria-label={tr('pages.dashboard.requestTrendTimeRange')}>
              {[24, 7, 14, 30].map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`db-range-btn ${trendRange === r ? 'active' : ''}`}
                  onClick={() => setTrendRange(r as 24 | 7 | 14 | 30)}
                >{r === 24 ? tr('pages.dashboard.24h') : tr('pages.dashboard.daysShort', { count: r })}</button>
              ))}
            </div>
          </div>
          {(() => {
            const W = 500; const H = 140; const PAD = 28;
            const maxV = Math.max(...trendData.map(d => d.total), 1);
            if (trendData.length < 2) return <div className="db-empty"><Icon name="checkCircle" /><p>{tr('pages.dashboard.notEnoughDataYet')}</p></div>;

            const xs = trendData.map((_, i) => PAD + (i / (trendData.length - 1)) * (W - PAD * 2));
            const ysTotal = trendData.map(d => PAD + (1 - d.total / maxV) * (H - PAD * 2));
            const ysViol = trendData.map(d => PAD + (1 - d.violations / maxV) * (H - PAD * 2));

            const totalLine = buildSmoothPath(xs, ysTotal);
            const violLine = buildSmoothPath(xs, ysViol);
            const totalArea = [
              totalLine,
              `L ${xs[xs.length - 1]},${H - PAD}`,
              `L ${xs[0]},${H - PAD}`,
              'Z',
            ].join(' ');

            const activeIndex = trendPinnedIndex ?? trendHoverIndex;
            const active = activeIndex !== null ? trendData[activeIndex] : null;
            const activeX = activeIndex !== null ? xs[activeIndex] : null;
            const activeTotalY = activeIndex !== null ? ysTotal[activeIndex] : null;
            const activeViolY = activeIndex !== null ? ysViol[activeIndex] : null;

            const handleTrendHover = (e: React.MouseEvent<SVGSVGElement>) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const rel = (e.clientX - rect.left) / Math.max(rect.width, 1);
              const idx = Math.min(trendData.length - 1, Math.max(0, Math.round(rel * (trendData.length - 1))));
              setTrendHoverIndex(idx);
            };

            const handleTrendClick = (e: React.MouseEvent<SVGSVGElement>) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const rel = (e.clientX - rect.left) / Math.max(rect.width, 1);
              const idx = Math.min(trendData.length - 1, Math.max(0, Math.round(rel * (trendData.length - 1))));
              setTrendPinnedIndex((prev) => (prev === idx ? null : idx));
              setTrendHoverIndex(idx);
            };

            return (
              <div className="db-line-wrap">
                <svg
                  viewBox={`0 0 ${W} ${H}`}
                  className="db-line-svg"
                  preserveAspectRatio="none"
                  onMouseMove={handleTrendHover}
                  onClick={handleTrendClick}
                  onMouseLeave={() => setTrendHoverIndex(null)}
                >
                  <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02" />
                    </linearGradient>
                    <linearGradient id="violGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--danger)" stopOpacity="0.18" />
                      <stop offset="100%" stopColor="var(--danger)" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>
                  {[0, 0.25, 0.5, 0.75, 1].map(f => (
                    <React.Fragment key={f}>
                      <line x1={PAD} y1={PAD + (1 - f) * (H - PAD * 2)} x2={W - PAD} y2={PAD + (1 - f) * (H - PAD * 2)} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="3 3" />
                      <text x={PAD - 4} y={PAD + (1 - f) * (H - PAD * 2) + 3} className="db-chart-label" textAnchor="end">{Math.round(maxV * f)}</text>
                    </React.Fragment>
                  ))}
                  <path d={totalArea} fill="url(#trendGrad)" />
                  <path
                    d={[
                      violLine,
                      `L ${xs[xs.length - 1]},${H - PAD}`,
                      `L ${xs[0]},${H - PAD}`,
                      'Z',
                    ].join(' ')}
                    fill="url(#violGrad)"
                  />
                  <path d={totalLine} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                  <path d={violLine} fill="none" stroke="var(--danger)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="4 2" />

                  {activeX !== null && activeTotalY !== null && activeViolY !== null && (
                    <>
                      <line x1={activeX} y1={PAD} x2={activeX} y2={H - PAD} className="db-trend-guide" />
                      <circle cx={activeX} cy={activeTotalY} r="4.5" className="db-trend-point db-trend-point-total" />
                      <circle cx={activeX} cy={activeViolY} r="4" className="db-trend-point db-trend-point-viol" />
                    </>
                  )}
                </svg>

                {activeIndex !== null && active && (
                  <div className="db-trend-tooltip" style={{ left: `${(activeIndex / (trendData.length - 1)) * 100}%` }}>
                    <div className="db-trend-date">{active.date}</div>
                    <div className="db-trend-row"><span className="dot total" /> {tr('pages.dashboard.total')} <strong>{active.total}</strong></div>
                    <div className="db-trend-row"><span className="dot viol" /> {tr('pages.dashboard.violations')} <strong>{active.violations}</strong></div>
                    <div className="db-trend-row"><span className="dot err" /> {tr('pages.dashboard.errors')} <strong>{active.errors}</strong></div>
                    {trendPinnedIndex !== null && <div className="db-trend-pin-note">{tr('pages.dashboard.pinnedClickChartToUnpin')}</div>}
                    <button
                      className="db-trend-drill-btn"
                      onClick={(e) => { e.stopPropagation(); drillTrend(activeIndex); }}
                    >
                      <Icon name="logs" size={12} /> {t('pages.dashboard.viewRecords', { count: active.total })}
                    </button>
                  </div>
                )}

                <div className="db-line-labels">
                  {trendData.map((d, i) => (i % 2 === 0 || trendData.length <= 7) ? <span key={i}>{d.date.slice(5)}</span> : <span key={i}></span>)}
                </div>
                <div className="db-chart-legend">
                  <span><span className="db-legend-line" style={{ background: 'var(--accent)' }} /> {tr('pages.dashboard.total')}</span>
                  <span><span className="db-legend-line db-legend-dashed" style={{ background: 'var(--danger)' }} /> {tr('pages.dashboard.violations')}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Request Status Distribution */}
        <div className="db-card db-alerts-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.requestOutcomes')}</h3>
            {byStatus.length > 0 && <span className="db-badge">{tr('pages.dashboard.clickToDrill')}</span>}
          </div>
          {(() => {
            // Build slices alongside a parallel raw-status list so clicks map
            // back to the exact status. Synthesized "Other"/"No data" have no
            // single status and are marked non-drillable (null).
            const TOP = 6;
            const rawStatus: (string | null)[] = [];
            const slices = byStatus.length > 0 ? (() => {
              const sorted = [...byStatus].sort((a, b) => b.count - a.count);
              const top = sorted.slice(0, TOP).map(s => {
                rawStatus.push(s.status);
                return {
                  label: s.status.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase()).replace(/\bdlp\b/i, 'DLP'),
                  value: s.count,
                  color: statusColor(s.status),
                };
              });
              const restCount = sorted.slice(TOP).reduce((sum, s) => sum + s.count, 0);
              if (restCount > 0) {
                rawStatus.push(null);
                top.push({ label: `Other (${sorted.length - TOP})`, value: restCount, color: 'var(--text-3)' });
              }
              return top;
            })() : (rawStatus.push(null), [{ label: t('pages.dashboard.noData'), value: 1, color: 'var(--border)' }]);
            return (
              <PieChart
                centerLabel="requests"
                slices={slices}
                onSliceClick={(_slice, index) => {
                  const s = rawStatus[index];
                  if (s) drillStatus(s);
                }}
              />
            );
          })()}
        </div>

        {/* Risk Score gauge */}
        <div className="db-card db-servers-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.riskScore')}</h3>
          </div>
          <div className="db-gauge-wrap">
            <svg className="db-gauge-svg" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" className="db-gauge-bg" />
              <circle
                cx="50" cy="50" r="46"
                className="db-gauge-prog"
                style={{
                  stroke: riskLevel.color,
                  strokeDasharray: `${(riskScore / 100) * circumference} ${circumference}`,
                }}
              />
              <text x="50" y="46" className="db-gauge-pct" style={{ fill: riskLevel.color }}>
                {riskScore}%
              </text>
              <text x="50" y="60" className="db-gauge-lbl">
                {riskLevel.level}
              </text>
            </svg>
          </div>
          <div className="db-gauge-legend">
            <span className="db-legend-item" title="0-16"><span className="db-dot" style={{ background: '#00B88A' }} /> {tr('pages.dashboard.veryLow17')}</span>
            <span className="db-legend-item" title="17-33"><span className="db-dot" style={{ background: '#79C6C7' }} /> {tr('pages.dashboard.low1733')}</span>
            <span className="db-legend-item" title="34-50"><span className="db-dot" style={{ background: '#F5A623' }} /> {tr('pages.dashboard.med3450')}</span>
            <span className="db-legend-item" title="51-67"><span className="db-dot" style={{ background: '#F9CF8B' }} /> {tr('pages.dashboard.high5167')}</span>
            <span className="db-legend-item" title="68-84"><span className="db-dot" style={{ background: '#EA5D5D' }} /> {tr('pages.dashboard.crit6884')}</span>
            <span className="db-legend-item" title="85-100"><span className="db-dot" style={{ background: '#E40046' }} /> {tr('pages.dashboard.highlyCrit85')}</span>
          </div>
        </div>
      </motion.div>

      {/* ── Row 3: Hourly Heatmap + Tool Usage + Agent Breakdown ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="db-mid-row"
      >
        {/* 24-hour Activity */}
        <div className="db-card db-heatmap-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.activityByHour')}</h3>
            <span className="db-badge">{tr('pages.dashboard.last24h')}</span>
          </div>
          {(() => {
            const maxH = Math.max(...hourly, 1);
            const peakHour = hourly.findIndex((v) => v === maxH);
            return (
              <p className="db-hourly-help">
                {tr('pages.dashboard.highestActivityAt')} <strong>{formatHourLabel(peakHour, false)}</strong> {tr('pages.dashboard.with')} <strong>{maxH}</strong> {tr('pages.dashboard.requestsHoverAnyBarToSee')}
              </p>
            );
          })()}
          <div className="db-heatmap-wrap">
            {(() => {
              const maxH = Math.max(...hourly, 1);
              return (
                <div className="db-heatmap-grid">
                  {hourly.map((v, i) => {
                    const intensity = v / maxH;
                    const hour = i;
                    const label = formatHourLabel(hour, true);
                    return (
                      <div
                        key={i}
                        className={`db-heatmap-cell ${hourlyHoverIndex === i ? 'active' : ''}`}
                        onMouseEnter={() => setHourlyHoverIndex(i)}
                        onMouseLeave={() => setHourlyHoverIndex(null)}
                        aria-label={t('pages.dashboard.hourRequests', { hour: formatHourLabel(hour, false), count: v })}
                      >
                        <div
                          className="db-heatmap-bar"
                          style={{
                            height: `${Math.max(4, intensity * 100)}%`,
                            opacity: Math.max(0.15, intensity),
                            background: v > 0 ? 'var(--accent)' : 'var(--border)',
                          }}
                        />
                        {i % 6 === 0 && <span className="db-heatmap-label">{label}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {hourlyHoverIndex !== null && (
              <div className="db-hourly-tooltip">
                <div className="db-trend-date">{formatHourLabel(hourlyHoverIndex, false)}</div>
                <div className="db-trend-row"><span className="dot total" /> {tr('pages.dashboard.requests')} <strong>{hourly[hourlyHoverIndex]}</strong></div>
              </div>
            )}
          </div>
        </div>

        {/* Tool Usage Breakdown */}
        <div className="db-card db-tool-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.toolUsage')}</h3>
            <span className="db-badge">{tr('pages.dashboard.tools', { v0: byTool.length })}</span>
          </div>
          {byTool.length > 0 ? (
            <div className="db-bar-list">
              {byTool.slice(0, 6).map((t) => {
                const pct = stats?.total_requests ? Math.round((t.count / stats.total_requests) * 100) : 0;
                const errorPct = t.count > 0 ? Math.round((t.errors / t.count) * 100) : 0;
                return (
                  <div
                    key={t.tool}
                    className="db-bar-row db-drill-bar"
                    role="button"
                    tabIndex={0}
                    onClick={() => drillTool(t.tool)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drillTool(t.tool); } }}
                    title={tr('pages.dashboard.drillInto', { name: t.tool })}
                  >
                    <div className="db-bar-label">
                      <span className="db-bar-name" title={t.tool}>{t.tool.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bDlp\b/, 'DLP')}</span>
                      <span className="db-bar-count">{t.count} <span className="db-bar-pct">({pct}%)</span><Icon name="chevronRight" size={13} /></span>
                    </div>
                    <div className="db-bar-track">
                      <div className="db-bar-fill" style={{ width: `${pct}%` }} />
                      {errorPct > 0 && <div className="db-bar-error" style={{ width: `${errorPct}%` }} title={tr('pages.dashboard.errorsCount', { count: t.errors })} />}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="db-empty"><Icon name="checkCircle" /><p>{tr('pages.dashboard.noToolUsageData')}</p></div>
          )}
        </div>

        {/* Agent Distribution */}
        <div className="db-card db-agent-dist-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.agents')}</h3>
            <span className="db-badge">{tr('pages.dashboard.total2', { v0: stats?.total_agents || 0 })}</span>
          </div>
          <div className="db-agent-charts">
            {agentsByStatus.length > 0 ? (
              <>
                <div className="db-mini-pie">
                  <h4>{tr('pages.dashboard.byStatus')}</h4>
                  <PieChart
                    radius={34}
                    centerLabel="agents"
                    slices={agentsByStatus.map(a => ({
                      label: a.status.replace(/^\w/, c => c.toUpperCase()),
                      value: a.count,
                      color: a.status === 'active' ? '#44536A' : a.status === 'pending' ? 'var(--warning)' : a.status === 'suspended' ? 'var(--danger)' : 'var(--text-3)',
                    }))}
                  />
                </div>
                {agentsByRole.length > 0 && (
                  <div className="db-mini-pie">
                    <h4>{tr('pages.dashboard.byRole')}</h4>
                    <PieChart
                      radius={34}
                      centerLabel="roles"
                      slices={agentsByRole.map((a, i) => ({
                        label: a.role.replace(/^\w/, c => c.toUpperCase()),
                        value: a.count,
                        color: ['#EEF2F6', 'var(--success)', 'var(--warning)', 'var(--info)'][i % 4],
                      }))}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="db-empty"><Icon name="users" /><p>{tr('pages.dashboard.noAgentsRegistered')}</p></div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ── Row 4: Recent Activity + Services Health ── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="db-bot-row"
      >
        {/* Recent Activity Feed */}
        <div className="db-card db-activity-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.recentActivity')}</h3>
            <span className="db-badge">{tr('pages.dashboard.total2', { v0: stats?.total_requests || 0 })}</span>
          </div>
          {recent.length > 0 ? (
            <div className="db-act-list">
              {recent.map((event, eventIdx) => {
                const cls = statusBadgeClass(event.status);
                const rowId = String(event.id);
                const isOpen = expandedRow === rowId;
                const shortAgent = event.agent_id && event.agent_id !== 'system'
                  ? event.agent_id.substring(0, 16) + '...'
                  : 'System';
                const timeAgo = (() => {
                  const diff = Date.now() - new Date(event.timestamp).getTime();
                  if (diff < 60000) return 'just now';
                  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
                  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
                  return `${Math.floor(diff / 86400000)}d ago`;
                })();

                return (
                  <motion.div
                    key={rowId}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: eventIdx * 0.05 }}
                  >
                    <div className="db-act-row" onClick={() => setExpandedRow(isOpen ? null : rowId)}>
                      <div className="db-act-icon-wrap">
                        <Icon name={event.status === 'success' || event.status === 'policy_allowed' ? 'checkCircle' : 'warning'} size={16} />
                      </div>
                      <div className="db-act-info">
                        <p className="db-act-name">{(event.tool_name || event.service || 'Request').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bDlp\b/, 'DLP')}</p>
                        <p className="db-act-task">{shortAgent} &middot; {timeAgo}</p>
                      </div>
                      <span className={`db-pill db-pill-${cls}`}>{event.status.replace('_', ' ').replace(/^\w/, c => c.toUpperCase()).replace(/\bdlp\b/i, 'DLP')}</span>
                    </div>
                    {isOpen && (
                      <div className="db-payload">
                        <div className="db-payload-head">
                          <Icon name="document" /> {tr('pages.dashboard.details')}
                          <CopyButton value={JSON.stringify(event, null, 2)} title={tr('pages.dashboard.copyJSON')} />
                        </div>
                        <div className="db-payload-grid">
                          <div><strong>{tr('pages.dashboard.service')}</strong> {event.service}</div>
                          <div><strong>{tr('pages.dashboard.tool')}</strong> {event.tool_name}</div>
                          <div><strong>{tr('pages.dashboard.agent')}</strong> {event.agent_id}</div>
                          <div><strong>{tr('pages.dashboard.time')}</strong> {parseServerDate(event.timestamp).toLocaleString()}</div>
                        </div>
                        <p className="db-payload-result"><strong>{tr('pages.dashboard.result')}</strong> {event.result}</p>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="db-empty"><Icon name="checkCircle" /><p>{tr('pages.dashboard.noRecentActivity')}</p></div>
          )}
        </div>

        {/* Services health */}
        <div className="db-card db-gauge-card">
          <div className="db-card-head">
            <h3>{tr('pages.dashboard.servicesHealth')}</h3>
          </div>
          <div className="db-server-list">
            {serverItems.map((s, i) => (
              <div key={i} className="db-server-row">
                <div className="db-server-icon">
                  <Icon name={s.icon} />
                </div>
                <div className="db-server-info">
                  <p className="db-server-name">{s.name}</p>
                  <p className="db-server-state">{s.state}</p>
                </div>
                <span className={`db-dot ${s.healthy ? 'db-dot-allow' : 'db-dot-warn'}`} />
              </div>
            ))}
          </div>

          {/* Service Request Distribution */}
          {byService.length > 0 && (
            <>
              <div className="db-card-head" style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <h3>{tr('pages.dashboard.byService')}</h3>
              </div>
              <div className="db-bar-list">
                {byService.slice(0, 5).map((s) => {
                  const pct = stats?.total_requests ? Math.round((s.count / stats.total_requests) * 100) : 0;
                  return (
                    <div
                      key={s.service}
                      className="db-bar-row db-drill-bar"
                      role="button"
                      tabIndex={0}
                      onClick={() => drillService(s.service)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drillService(s.service); } }}
                      title={t('pages.dashboard.drillInto', { name: s.service })}
                    >
                      <div className="db-bar-label">
                        <span className="db-bar-name">{s.service}</span>
                        <span className="db-bar-count">{s.count}<Icon name="chevronRight" size={13} /></span>
                      </div>
                      <div className="db-bar-track">
                        <div className="db-bar-fill db-bar-fill-service" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </motion.div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
